import React, { useEffect, useState } from 'react';
import { MapPinIcon, CheckCircleIcon, AlertCircleIcon, ClockIcon, CarIcon, GaugeIcon, FuelIcon, PhoneIcon, SearchIcon, FilterIcon, RefreshCwIcon, LogInIcon } from 'lucide-react';
import { GasStation } from '../data/stations';
import { loadFromDatabase } from '../utils/database';
import { Link } from 'react-router-dom';
interface Vehicle {
  id: string;
  status: 'waiting' | 'in-service' | 'completed';
}
export function PublicStationsView() {
  const [stations, setStations] = useState<GasStation[]>([]);
  const [stationQueues, setStationQueues] = useState<Record<string, Vehicle[]>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRiver, setSelectedRiver] = useState<'all' | 'RG' | 'RD'>('all');
  const [selectedCommune, setSelectedCommune] = useState<string>('all');
  const [showOnlyOperational, setShowOnlyOperational] = useState(false);
  const [showOnlyWithFuel, setShowOnlyWithFuel] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const loadData = () => {
    const savedData = loadFromDatabase();
    if (savedData.stations && savedData.stations.length > 0) {
      setStations(savedData.stations);
    }
    if (savedData.stationQueues) {
      setStationQueues(savedData.stationQueues);
    }
    setLastUpdate(new Date());
  };
  useEffect(() => {
    loadData();
    // Auto-refresh toutes les 30 secondes
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);
  const getQueueStats = (stationId: string) => {
    const queue = stationQueues[stationId] || [];
    const waiting = queue.filter(v => v.status === 'waiting').length;
    const inService = queue.filter(v => v.status === 'in-service').length;
    return {
      waiting,
      inService,
      total: queue.length
    };
  };
  const filteredStations = stations.filter(station => {
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      const matchesName = station.name.toLowerCase().includes(search);
      const matchesLocation = station.location.toLowerCase().includes(search);
      const matchesCommune = station.commune.toLowerCase().includes(search);
      if (!matchesName && !matchesLocation && !matchesCommune) {
        return false;
      }
    }
    if (selectedRiver !== 'all' && station.riverSide !== selectedRiver) {
      return false;
    }
    if (selectedCommune !== 'all' && station.commune !== selectedCommune) {
      return false;
    }
    if (showOnlyOperational && !station.isOperational) {
      return false;
    }
    if (showOnlyWithFuel) {
      if (!station.fuelAvailability.essence && !station.fuelAvailability.diesel) {
        return false;
      }
    }
    return true;
  });
  const allCommunes = Array.from(new Set(stations.map(s => s.commune))).sort();
  const operationalStations = filteredStations.filter(s => s.isOperational).length;
  const totalVehicles = Object.values(stationQueues).reduce((sum, queue) => sum + queue.length, 0);
  return <div className="w-full min-h-screen bg-gradient-to-br from-blue-50 to-slate-100" data-id="element-108">
      <div className="max-w-7xl mx-auto p-6" data-id="element-109">
        {/* CECOGEC Header */}
        <div className="bg-gradient-to-r from-green-700 via-yellow-600 to-red-700 rounded-2xl shadow-lg p-4 mb-4" data-id="element-110">
          <div className="flex items-center justify-center gap-4" data-id="element-111">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/bKrn82xDGtf6CD4q31okdQ/Coat_of_arms_of_Mali.svg" alt="Emblème du Mali" className="w-16 h-16 object-contain" data-id="element-112" />
            <h1 className="text-center text-white text-2xl md:text-3xl font-bold" data-id="element-113">
              Centre de Coordination et de Gestion des Crises et Catastrophes
              (CECOGEC)
            </h1>
          </div>
        </div>
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6" data-id="element-114">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4" data-id="element-115">
            <div className="flex items-center gap-3" data-id="element-116">
              <div className="bg-blue-600 p-3 rounded-xl" data-id="element-117">
                <MapPinIcon className="w-8 h-8 text-white" data-id="element-118" />
              </div>
              <div data-id="element-119">
                <h2 className="text-3xl font-bold text-gray-900" data-id="element-120">
                  État des Stations Service
                </h2>
                <p className="text-gray-500" data-id="element-121">Consultation Publique - Bamako</p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-122">
              <button onClick={loadData} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-xl font-semibold transition-colors shadow-lg" data-id="element-123">
                <RefreshCwIcon className="w-5 h-5" data-id="element-124" />
                <span className="hidden sm:inline" data-id="element-125">Actualiser</span>
              </button>
              <Link to="/admin" className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-xl font-semibold transition-colors shadow-lg" data-id="element-126">
                <LogInIcon className="w-5 h-5" data-id="element-127" />
                <span className="hidden sm:inline" data-id="element-128">Connexion Staff</span>
              </Link>
            </div>
          </div>
          {/* Last Update */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-6" data-id="element-129">
            <ClockIcon className="w-4 h-4" data-id="element-130" />
            <span data-id="element-131">
              Dernière mise à jour:{' '}
              {lastUpdate.toLocaleTimeString('fr-FR', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            })}
            </span>
          </div>
          {/* Search and Filters */}
          <div className="space-y-4 mb-6" data-id="element-132">
            <div className="relative" data-id="element-133">
              <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-134" />
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Rechercher par nom, localisation ou commune..." className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors" data-id="element-135" />
            </div>
            <div className="flex items-center gap-3 flex-wrap" data-id="element-136">
              <div className="flex items-center gap-2" data-id="element-137">
                <FilterIcon className="w-4 h-4 text-gray-600" data-id="element-138" />
                <span className="text-sm font-semibold text-gray-700" data-id="element-139">
                  Filtres:
                </span>
              </div>
              <select value={selectedRiver} onChange={e => setSelectedRiver(e.target.value as 'all' | 'RG' | 'RD')} className="px-4 py-2 border-2 border-gray-200 rounded-lg font-semibold focus:outline-none focus:border-blue-500 transition-colors" data-id="element-140">
                <option value="all" data-id="element-141">Toutes les Rives</option>
                <option value="RG" data-id="element-142">Rive Gauche</option>
                <option value="RD" data-id="element-143">Rive Droite</option>
              </select>
              <select value={selectedCommune} onChange={e => setSelectedCommune(e.target.value)} className="px-4 py-2 border-2 border-gray-200 rounded-lg font-semibold focus:outline-none focus:border-blue-500 transition-colors" data-id="element-144">
                <option value="all" data-id="element-145">Toutes les Communes</option>
                {allCommunes.map(commune => <option key={commune} value={commune} data-id="element-146">
                    {commune}
                  </option>)}
              </select>
              <button onClick={() => setShowOnlyOperational(!showOnlyOperational)} className={`px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 ${showOnlyOperational ? 'bg-green-600 text-white' : 'border-2 border-gray-200 text-gray-700 hover:bg-gray-50'}`} data-id="element-147">
                <CheckCircleIcon className="w-4 h-4" data-id="element-148" />
                Opérationnelles
              </button>
              <button onClick={() => setShowOnlyWithFuel(!showOnlyWithFuel)} className={`px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 ${showOnlyWithFuel ? 'bg-blue-600 text-white' : 'border-2 border-gray-200 text-gray-700 hover:bg-gray-50'}`} data-id="element-149">
                <FuelIcon className="w-4 h-4" data-id="element-150" />
                Carburant Disponible
              </button>
              {(searchTerm || selectedRiver !== 'all' || selectedCommune !== 'all' || showOnlyOperational || showOnlyWithFuel) && <button onClick={() => {
              setSearchTerm('');
              setSelectedRiver('all');
              setSelectedCommune('all');
              setShowOnlyOperational(false);
              setShowOnlyWithFuel(false);
            }} className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold transition-colors" data-id="element-151">
                  Réinitialiser
                </button>}
            </div>
            {(searchTerm || selectedRiver !== 'all' || selectedCommune !== 'all' || showOnlyOperational || showOnlyWithFuel) && <div className="text-sm text-gray-600" data-id="element-152">
                {filteredStations.length} station
                {filteredStations.length !== 1 ? 's' : ''} trouvée
                {filteredStations.length !== 1 ? 's' : ''}
              </div>}
          </div>
          {/* Global Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-153">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200" data-id="element-154">
              <div className="flex items-center gap-3" data-id="element-155">
                <div className="bg-blue-600 p-3 rounded-lg" data-id="element-156">
                  <MapPinIcon className="w-6 h-6 text-white" data-id="element-157" />
                </div>
                <div data-id="element-158">
                  <p className="text-sm text-blue-700 font-medium" data-id="element-159">
                    Stations Opérationnelles
                  </p>
                  <p className="text-2xl font-bold text-blue-900" data-id="element-160">
                    {operationalStations} / {filteredStations.length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200" data-id="element-161">
              <div className="flex items-center gap-3" data-id="element-162">
                <div className="bg-green-600 p-3 rounded-lg" data-id="element-163">
                  <CarIcon className="w-6 h-6 text-white" data-id="element-164" />
                </div>
                <div data-id="element-165">
                  <p className="text-sm text-green-700 font-medium" data-id="element-166">
                    Total Véhicules
                  </p>
                  <p className="text-2xl font-bold text-green-900" data-id="element-167">
                    {totalVehicles}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl border border-purple-200" data-id="element-168">
              <div className="flex items-center gap-3" data-id="element-169">
                <div className="bg-purple-600 p-3 rounded-lg" data-id="element-170">
                  <GaugeIcon className="w-6 h-6 text-white" data-id="element-171" />
                </div>
                <div data-id="element-172">
                  <p className="text-sm text-purple-700 font-medium" data-id="element-173">
                    Taux d'Occupation
                  </p>
                  <p className="text-2xl font-bold text-purple-900" data-id="element-174">
                    {stations.length > 0 ? Math.round(totalVehicles / stations.length * 10) / 10 : 0}
                    /station
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Stations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-175">
          {filteredStations.length === 0 ? <div className="col-span-full text-center py-12 bg-white rounded-xl" data-id="element-176">
              <SearchIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" data-id="element-177" />
              <p className="text-gray-500 text-lg" data-id="element-178">Aucune station trouvée</p>
              <p className="text-gray-400 text-sm mt-2" data-id="element-179">
                Essayez de modifier vos critères de recherche
              </p>
            </div> : filteredStations.map(station => {
          const stats = getQueueStats(station.id);
          return <div key={station.id} className={`bg-white border-2 rounded-xl p-5 transition-all hover:shadow-lg ${station.isOperational ? 'border-blue-200 hover:border-blue-400' : 'border-gray-300 bg-gray-50'}`} data-id="element-180">
                  {/* Station Header */}
                  <div className="flex items-start justify-between mb-4" data-id="element-181">
                    <div className="flex-1" data-id="element-182">
                      <div className="flex items-center gap-2 mb-2" data-id="element-183">
                        <h3 className="font-bold text-gray-900 text-lg" data-id="element-184">
                          {station.name}
                        </h3>
                        {station.isOperational ? <CheckCircleIcon className="w-5 h-5 text-green-600" data-id="element-185" /> : <AlertCircleIcon className="w-5 h-5 text-red-600" data-id="element-186" />}
                      </div>
                      <p className="text-sm text-gray-600 mb-1" data-id="element-187">
                        {station.location}
                      </p>
                      <div className="flex items-center gap-2" data-id="element-188">
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-md" data-id="element-189">
                          {station.commune}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-md font-semibold ${station.riverSide === 'RG' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`} data-id="element-190">
                          {station.riverSide === 'RG' ? 'Rive Gauche' : 'Rive Droite'}
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Queue Stats */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 mb-4 border border-blue-200" data-id="element-191">
                    <p className="text-xs font-semibold text-blue-900 mb-3" data-id="element-192">
                      FILE D'ATTENTE
                    </p>
                    <div className="grid grid-cols-3 gap-3" data-id="element-193">
                      <div className="text-center" data-id="element-194">
                        <div className="bg-white rounded-lg p-2 mb-1" data-id="element-195">
                          <ClockIcon className="w-5 h-5 text-yellow-600 mx-auto" data-id="element-196" />
                        </div>
                        <p className="text-xs text-gray-600 mb-1" data-id="element-197">En Attente</p>
                        <p className="text-xl font-bold text-yellow-600" data-id="element-198">
                          {stats.waiting}
                        </p>
                      </div>
                      <div className="text-center" data-id="element-199">
                        <div className="bg-white rounded-lg p-2 mb-1" data-id="element-200">
                          <GaugeIcon className="w-5 h-5 text-green-600 mx-auto" data-id="element-201" />
                        </div>
                        <p className="text-xs text-gray-600 mb-1" data-id="element-202">En Service</p>
                        <p className="text-xl font-bold text-green-600" data-id="element-203">
                          {stats.inService}
                        </p>
                      </div>
                      <div className="text-center" data-id="element-204">
                        <div className="bg-white rounded-lg p-2 mb-1" data-id="element-205">
                          <CarIcon className="w-5 h-5 text-blue-600 mx-auto" data-id="element-206" />
                        </div>
                        <p className="text-xs text-gray-600 mb-1" data-id="element-207">Total</p>
                        <p className="text-xl font-bold text-blue-600" data-id="element-208">
                          {stats.total}
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Station Info */}
                  <div className="space-y-2 mb-4" data-id="element-209">
                    <div className="flex items-center justify-between text-sm" data-id="element-210">
                      <span className="text-gray-600" data-id="element-211">Pompes</span>
                      <span className="font-bold text-gray-900" data-id="element-212">
                        {station.pumps}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm" data-id="element-213">
                      <span className="text-gray-600" data-id="element-214">Capacité</span>
                      <span className="font-bold text-gray-900" data-id="element-215">
                        {(station.capacity / 1000).toFixed(0)}k Litres
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm" data-id="element-216">
                      <ClockIcon className="w-4 h-4 text-gray-500" data-id="element-217" />
                      <span className="text-gray-600" data-id="element-218">
                        {station.openingHours}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm" data-id="element-219">
                      <PhoneIcon className="w-4 h-4 text-gray-500" data-id="element-220" />
                      <span className="text-gray-600" data-id="element-221">
                        {station.managerContact}
                      </span>
                    </div>
                  </div>
                  {/* Fuel Availability */}
                  <div className="pt-4 border-t border-gray-200" data-id="element-222">
                    <p className="text-xs font-semibold text-gray-500 mb-3" data-id="element-223">
                      DISPONIBILITÉ CARBURANT
                    </p>
                    <div className="space-y-2" data-id="element-224">
                      <div className="flex items-center justify-between" data-id="element-225">
                        <div className="flex items-center gap-2" data-id="element-226">
                          <FuelIcon className="w-4 h-4 text-blue-600" data-id="element-227" />
                          <span className="text-sm text-gray-700" data-id="element-228">Essence</span>
                          <span className="text-xs font-bold text-blue-900" data-id="element-229">
                            {station.prices.essence} FCFA/L
                          </span>
                        </div>
                        {station.fuelAvailability.essence ? <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded flex items-center gap-1" data-id="element-230">
                            <CheckCircleIcon className="w-3 h-3" data-id="element-231" />
                            Disponible
                          </span> : <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded flex items-center gap-1" data-id="element-232">
                            <AlertCircleIcon className="w-3 h-3" data-id="element-233" />
                            Rupture
                          </span>}
                      </div>
                      <div className="flex items-center justify-between" data-id="element-234">
                        <div className="flex items-center gap-2" data-id="element-235">
                          <FuelIcon className="w-4 h-4 text-orange-600" data-id="element-236" />
                          <span className="text-sm text-gray-700" data-id="element-237">Diesel</span>
                          <span className="text-xs font-bold text-orange-900" data-id="element-238">
                            {station.prices.diesel} FCFA/L
                          </span>
                        </div>
                        {station.fuelAvailability.diesel ? <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded flex items-center gap-1" data-id="element-239">
                            <CheckCircleIcon className="w-3 h-3" data-id="element-240" />
                            Disponible
                          </span> : <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded flex items-center gap-1" data-id="element-241">
                            <AlertCircleIcon className="w-3 h-3" data-id="element-242" />
                            Rupture
                          </span>}
                      </div>
                      {station.prices.gpl && <div className="flex items-center justify-between" data-id="element-243">
                          <div className="flex items-center gap-2" data-id="element-244">
                            <FuelIcon className="w-4 h-4 text-purple-600" data-id="element-245" />
                            <span className="text-sm text-gray-700" data-id="element-246">GPL</span>
                            <span className="text-xs font-bold text-purple-900" data-id="element-247">
                              {station.prices.gpl} FCFA/L
                            </span>
                          </div>
                          {station.fuelAvailability.gpl ? <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded flex items-center gap-1" data-id="element-248">
                              <CheckCircleIcon className="w-3 h-3" data-id="element-249" />
                              Disponible
                            </span> : <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded flex items-center gap-1" data-id="element-250">
                              <AlertCircleIcon className="w-3 h-3" data-id="element-251" />
                              Rupture
                            </span>}
                        </div>}
                    </div>
                  </div>
                  {/* Status Badge */}
                  {!station.isOperational && <div className="mt-4 bg-red-100 border border-red-300 rounded-lg px-3 py-2" data-id="element-252">
                      <p className="text-xs font-bold text-red-800 text-center" data-id="element-253">
                        ⚠️ Station Hors Service
                      </p>
                    </div>}
                </div>;
        })}
        </div>
        {/* Footer */}
        <div className="mt-8 text-center text-gray-600" data-id="element-254">
          <p className="font-semibold" data-id="element-255">CECOGEC - République du Mali</p>
          <p className="text-sm mt-1" data-id="element-256">
            Centre de Coordination et de Gestion des Crises et Catastrophes
          </p>
        </div>
      </div>
    </div>;
}